import cn from 'classnames';
import { capitalize } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import { Card, CardItemSelector } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { MediaChannelStyled } from './MediaChannelSelector.styled';

type Props = {
  className?: string;
  id: string;
  onChange: (value: CampaignFamilyEnum) => void;
  options: CampaignFamilyEnum[];
  showTitle?: boolean;
  subTitle: string;
  title: string;
  value?: CampaignFamilyEnum;
};

const MEDIA_CHANNEL_ICONS: Record<CampaignFamilyEnum, string> = {
  [CampaignFamilyEnum.ctv]: 'fa-solid fa-screencast',
  [CampaignFamilyEnum.dooh]: 'fa-kit fa-screen',
  [CampaignFamilyEnum.standard]: 'fa-solid fa-laptop-mobile'
};

function MediaChannelSelector({ className, id, onChange, options, showTitle = true, subTitle, title, value }: Props) {
  const { formatMessage } = useIntl();

  const itemsConfig: React.ComponentProps<typeof CardItemSelector>['items'] = options.map(option => ({
    bottomBarSelected: true,
    description: formatMessage({
      defaultMessage: `${capitalize(option)} description`,
      id: `mediaChannelSelection.campaignTypes.${option}.subtitle`
    }),
    footerText: formatMessage({
      defaultMessage: `${capitalize(option)} footer`,
      id: `mediaChannelSelection.campaignTypes.${option}.footer`
    }),
    iconDefault: MEDIA_CHANNEL_ICONS[option],
    iconSelected: 'fas fa-check-circle',
    label: formatMessage({
      defaultMessage: capitalize(option),
      id: `mediaChannelSelection.campaignTypes.${option}.title`
    }),
    value: option
  }));

  return (
    <MediaChannelStyled className={cn('mediaChannelSelector', className)} id={id}>
      {showTitle && (
        <div className="mediaChannelSelector__header">
          <div className="mediaChannelSelector__headerIcon">
            <span className="fa-2x fas fa-light fa-bolt" />
          </div>
          <div className="mediaChannelSelector__headerTitle">{title}</div>
          <div className="mediaChannelSelector__headerSubTitle">{subTitle}</div>
        </div>
      )}
      <div className="mediaChannelSelector__cardContainer">
        {itemsConfig.map(item => (
          <Card
            className="mediaChannelSelector__infoCard"
            isSelected={value === item.value}
            key={item.value}
            onClick={() => onChange(item.value as CampaignFamilyEnum)}
          >
            <Card.Header align="center">
              <Card.Label title={item.footerText} variant="category">
                {item.footerText}
              </Card.Label>
            </Card.Header>
            <Card.Media
              className={cn('mediaChannelSelector__cardMedia', {
                'mediaChannelSelector__cardMedia--selected': value === item.value
              })}
              icon={item.iconDefault}
            />
            <Card.Body align="center">
              <Card.Label title={item.label} variant="title">
                {item.label}
              </Card.Label>
              <Card.Label
                className="mediaChannelSelector__cardDescription"
                ellipsis={false}
                title={item.description}
                variant="subtitle"
              >
                {item.description}
              </Card.Label>
            </Card.Body>
          </Card>
        ))}
      </div>
    </MediaChannelStyled>
  );
}

export { MediaChannelSelector, MEDIA_CHANNEL_ICONS };
